import { InputHTMLAttributes, useState } from "react";
import { useTranslation } from "react-i18next";

import { Eye, EyeSlash } from "@phosphor-icons/react";
import { defaultIconProps } from "../features/shared/ui/icons";

type PasswordFieldProps = Omit<InputHTMLAttributes<HTMLInputElement>, "type"> & {
  containerClassName?: string;
  showLabel?: string;
  hideLabel?: string;
  toggleTabIndex?: number;
};

export function PasswordField({
  containerClassName,
  className,
  showLabel,
  hideLabel,
  disabled,
  toggleTabIndex,
  ...inputProps
}: PasswordFieldProps) {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);

  const containerClasses = [
    "relative flex rounded-xl shadow-sm ring-1 ring-inset ring-slate-200 focus-within:ring-2 focus-within:ring-inset focus-within:ring-sky-600 dark:ring-slate-700",
    disabled ? "bg-slate-50 text-slate-500 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700" : "bg-white dark:bg-slate-900",
    containerClassName
  ]
    .filter(Boolean)
    .join(" ");

  const inputClasses = [
    "block w-full flex-1 rounded-xl border-0 bg-transparent py-2.5 pl-3 pr-10 text-slate-900 placeholder:text-slate-400 focus:ring-0 sm:text-sm sm:leading-6 disabled:cursor-not-allowed disabled:bg-transparent dark:text-slate-100 dark:placeholder-slate-500",
    className
  ]
    .filter(Boolean)
    .join(" ");

  const ariaLabel = isVisible
    ? hideLabel ?? t("forms.password.hide_label")
    : showLabel ?? t("forms.password.show_label");

  const resolvedTabIndex = disabled ? -1 : toggleTabIndex ?? -1;

  return (
    <div className={containerClasses}>
      <input
        {...inputProps}
        className={inputClasses}
        type={isVisible ? "text" : "password"}
        disabled={disabled}
      />
      <button
        type="button"
        className="absolute inset-y-0 right-0 flex items-center px-3 text-slate-400 hover:text-slate-600 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed dark:text-slate-500 dark:hover:text-slate-300"
        onClick={() => setIsVisible((prev) => !prev)}
        aria-label={ariaLabel}
        aria-pressed={isVisible}
        disabled={disabled}
        tabIndex={resolvedTabIndex}
      >
        {isVisible ? (
          <EyeSlash {...defaultIconProps} aria-hidden />
        ) : (
          <Eye {...defaultIconProps} aria-hidden />
        )}
      </button>
    </div>
  );
}
